/**
 * Pravidla veřejných poptávek BEZ `zod` — jediný domov čísel a znění, které potřebuje
 * serverové schéma (`schemas/inquiryFields.ts`, `schemas/customInquirySchema.ts`,
 * `inquirySchema` v `schemas/index.ts`) i validátor formulářů webu
 * (`apps/web/src/lib/inquiryValidation.ts`, klient přes `@ckgo2/shared/client`).
 *
 * Proč zvlášť: klientský kód webu nesmí za běhu dosáhnout na `zod` (řez 13, F10.5), ale
 * hlášky se vykreslují doslova pod poli — dvě kopie znění by se dřív nebo později rozešly
 * a týž zákazník by za tutéž chybu dostal dvě věty. Schéma i validátor proto čtou odsud;
 * paritu chování drží test webu proti skutečným schématům.
 *
 * Do barelu `index.ts` se nepřidává: jména mezí by se zdvojila s reexportem přes `schemas`.
 * Schémata (`schemas/index.ts`, `schemas/customInquirySchema.ts`) importují odsud přímo,
 * ne přes barel: ten per-doménové moduly reexportuje, takže import zpátky by byl cyklus.
 */

/** Strop poznámky (veřejný koncový bod nesmí přijmout neomezený text). */
export const NOTES_MAX_LENGTH = 2000;
export const NAME_MAX_LENGTH = 100;
export const PHONE_MIN_LENGTH = 5;
export const PHONE_MAX_LENGTH = 20;
/**
 * Strop počtu dětí — a tím i délky pole věků. Web podle něj clampuje počet vstupů věku
 * (`Array.from({ length: children })`), jinak by `1000000` v poli „Děti" zamrazilo stránku.
 */
export const MAX_CHILDREN = 10;
export const MAX_ADULTS = 20;
export const MIN_ADULTS = 1;
export const CHILD_AGE_MAX = 17;
export const DURATION_DAYS_MIN = 1;
export const DURATION_DAYS_MAX = 90;
/** Strop `clickSource` atribuce (ostatní stropy atribuce jsou v `utils`). */
export const CLICK_SOURCE_MAX_LENGTH = 32;
export const ORIGIN_PATH_MAX_LENGTH = 512;
/**
 * Tvar veřejné cesty původu. Druhé lomítko hned na začátku je PROTOKOL-RELATIVNÍ URL
 * (`//evil.example/x`), tedy cizí origin, ne cesta na našem webu.
 */
export const ORIGIN_PATH_SHAPE = /^\/(?!\/)[A-Za-z0-9\-._~/%]*$/;
/** Tvar data `YYYY-MM-DD` poptávky k zájezdu (kalendářní platnost hlídá `isIsoDate`). */
export const ISO_DATE_SHAPE = /^\d{4}-\d{2}-\d{2}$/;
export const DESTINATION_SLUG_MAX_LENGTH = 80;
export const CUSTOM_TRIP_ID_MAX_LENGTH = 64;
export const BUDGET_MAX_LENGTH = 120;
export const ROOM_ID_MAX_LENGTH = 60;

// Výčty poptávky k zájezdu / hotelu (`inquirySchema`): `z.enum(X)` na serveru, `oneOf(…, X)`
// ve validátoru webu. Hodnota přidaná sem platí na obou stranách najednou.
/** Volba termínu ve formuláři zájezdu (řez 01): vypsaný odjezd, nebo „Jiný termín". */
export const INQUIRY_DATE_CHOICES = ["departure", "other"] as const;
/**
 * Osa profilu LEADU (`fit|guided|stay|service`), ne katalogová osa `TRIP_TYPES` — stejný klíč
 * `tripType`, jiný číselník.
 */
export const INQUIRY_LEAD_TRIP_TYPES = ["fit", "guided", "stay", "service"] as const;
export const INQUIRY_FLIGHT_CLASSES = ["economy", "premium", "business"] as const;
export const INQUIRY_DATE_FLEXIBILITY = ["fixed", "plus_minus_week", "flexible"] as const;
export const INQUIRY_TRAVEL_EXPERIENCE = ["first_asia", "experienced", "expert"] as const;

/** Znění všech hlášek poptávek (vykreslují se doslova pod poli a v hlášce 400 API). */
export const INQUIRY_MESSAGES = {
  firstNameRequired: "Zadejte prosím jméno.",
  firstNameTooLong: `Jméno může mít nejvýš ${NAME_MAX_LENGTH} znaků.`,
  lastNameRequired: "Zadejte prosím příjmení.",
  lastNameTooLong: `Příjmení může mít nejvýš ${NAME_MAX_LENGTH} znaků.`,
  emailInvalid: "Zadejte prosím platný e-mail.",
  phoneRequired: "Zadejte prosím telefonní číslo.",
  phoneTooLong: `Telefonní číslo může mít nejvýš ${PHONE_MAX_LENGTH} znaků.`,
  adultsType: "Zadejte prosím počet dospělých.",
  adultsInt: "Počet dospělých musí být celé číslo.",
  adultsMin: "Cestovat musí aspoň jeden dospělý.",
  adultsMax: `Počet dospělých může být nejvýš ${MAX_ADULTS} — větší skupinu nám prosím napište do poznámky.`,
  childrenType: "Zadejte prosím počet dětí.",
  childrenInt: "Počet dětí musí být celé číslo.",
  childrenMin: "Počet dětí nemůže být záporný.",
  childrenMax: `Počet dětí může být nejvýš ${MAX_CHILDREN} — větší skupinu nám prosím napište do poznámky.`,
  childAgeType: "Zadejte prosím věk dítěte v letech.",
  childAgeInt: "Zadejte prosím věk dítěte v celých letech.",
  childAgeRange: `Věk dítěte musí být mezi 0 a ${CHILD_AGE_MAX} lety.`,
  childAgesTooMany: `Věk lze zadat nejvýš u ${MAX_CHILDREN} dětí.`,
  childAgesMissing: "Zadejte prosím věk každého dítěte.",
  notesTooLong: `Poznámka může mít nejvýš ${NOTES_MAX_LENGTH} znaků — delší zadání nám prosím napište e-mailem.`,
  attributionTooLong: "Údaj o zdroji návštěvy je příliš dlouhý.",
  dateInvalid: "Zadejte prosím platné datum.",
  durationType: "Zadejte prosím délku cesty ve dnech.",
  durationInt: "Zadejte prosím délku cesty v celých dnech.",
  durationRange: `Délka cesty musí být mezi ${DURATION_DAYS_MIN} a ${DURATION_DAYS_MAX} dny.`,
  whereOrWhenRequired: "Napište nám prosím aspoň to, kam nebo kdy chcete jet.",
  customDateOrder: "Konec termínu nemůže být dřív než jeho začátek.",
  requestedPeriodOrder: "Konec období nesmí být před jeho začátkem.",
} as const;

/**
 * Prázdná hodnota volitelného textového pole. DB řádky nesou NULL a react-hook-form drží ""
 * u nedotčeného vstupu — obojí znamená „nevyplněno", jinak by `z.string().email().optional()`
 * apod. odmítlo celé uložení chybou, kterou formulář ani nemusí ukázat (destination-teams bug).
 * Ve schématu: `z.preprocess(blankToUndefined, z.string()…optional())`.
 */
export const blankToUndefined = (v: unknown) => (v === null || v === "" ? undefined : v);

/**
 * Prázdná hodnota rolety nebo data poptávky k zájezdu / hotelu (`inquirySchema`): jen `""`
 * (výchozí hodnota formuláře) znamená nevyplněno; `null` zůstává chybou typu.
 */
export const emptyToUndefined = (v: unknown) => (v === "" ? undefined : v);

/**
 * Nevyplněný ČÍSELNÝ input posílá `NaN` (`valueAsNumber` nad prázdným polem), nevybraná
 * roleta `""`. Obojí znamená „nevyplněno", ne „neplatná hodnota" — bez tohohle kroku by
 * zákazník dostal hlášku u pole, kterého se ani nedotkl.
 */
export const blankNumberToUndefined = (v: unknown) =>
  v === "" || v === null || (typeof v === "number" && Number.isNaN(v)) ? undefined : v;

/** Vyplnil zákazník tohle ovládání věku dítěte? Nedotčené přijde jako `undefined` (roleta
 *  hotelového widgetu), `""` nebo `NaN` (číselný vstup formuláře). */
export const isFilledChildAge = (age: unknown): boolean =>
  age !== undefined &&
  age !== null &&
  age !== "" &&
  !(typeof age === "number" && Number.isNaN(age));
